import { LOCAL_STORE_ITEMS, SAVED_AI_FILE_ID } from '../constants';

// The AI imports this browser started and the server accepted (202 with their run), newest first, by
// their run ids only: the server tells the rest (GET /api/ai/image-bulk/:runId). Kept across reloads
// so the import page can come back to them instead of analysing a file again. Per browser on purpose:
// a shared account on another computer does not see them. A producer has one import at a time (a
// single id is stored); a superuser runs up to AI_IMPORT_MAX_RUNNING at once and keeps the last
// AI_IMPORT_KEPT in a list.
export const AI_IMPORT_MAX_RUNNING = 3;
const AI_IMPORT_KEPT = 10;
const RUN_ID = /^[0-9a-f]{24}$/i;

export const readAiRunIds = (): string[] => {
  try {
    const stored = localStorage.getItem(LOCAL_STORE_ITEMS.AI_IMPORT_RUN);
    if (!stored) return [];
    if (RUN_ID.test(stored)) return [stored];
    const list = JSON.parse(stored);
    return Array.isArray(list) ? list.filter((id): id is string => typeof id === 'string' && RUN_ID.test(id)) : [];
  } catch {
    return [];
  }
};

// The import a producer comes back to: the newest.
export const readAiRunId = (): string | null => readAiRunIds()[0] ?? null;

const writeAiRunIds = (runIds: string[]) => {
  try {
    if (!runIds.length) localStorage.removeItem(LOCAL_STORE_ITEMS.AI_IMPORT_RUN);
    else localStorage.setItem(LOCAL_STORE_ITEMS.AI_IMPORT_RUN, runIds.length === 1 ? runIds[0] : JSON.stringify(runIds));
  } catch {}
};

// keepOthers (a superuser): added in front of the others; otherwise it replaces them.
export const storeAiRunId = (runId: string, keepOthers = false) =>
  writeAiRunIds(keepOthers ? [runId, ...readAiRunIds().filter(id => id !== runId)].slice(0, AI_IMPORT_KEPT) : [runId]);

// One import (its products were imported, it failed, it is removed from the list), or all of them.
export const forgetAiRun = (runId?: string) =>
  writeAiRunIds(runId ? readAiRunIds().filter(id => id !== runId) : []);

// The import a superuser follows on the import step (its progress, until its review opens): the page
// comes back to it after being left. Its run id only.
export const readFollowedRunId = (): string | null => {
  try {
    const runId = localStorage.getItem(LOCAL_STORE_ITEMS.AI_IMPORT_FOLLOWED);
    return runId && RUN_ID.test(runId) ? runId : null;
  } catch {
    return null;
  }
};

export const setFollowedRunId = (runId: string | null) => {
  try {
    if (runId) localStorage.setItem(LOCAL_STORE_ITEMS.AI_IMPORT_FOLLOWED, runId);
    else localStorage.removeItem(LOCAL_STORE_ITEMS.AI_IMPORT_FOLLOWED);
  } catch {}
};

// "Seen": the bulk import page showed an import with its current status. A superuser's dashboard lists
// the imports not seen yet; a running one is never seen (its progress changes).
type TSeenStatuses = Record<string, string>;

export const readSeenStatuses = (): TSeenStatuses => {
  try {
    const seen = JSON.parse(localStorage.getItem(LOCAL_STORE_ITEMS.AI_IMPORT_SEEN) || '{}');
    return seen && typeof seen === 'object' && !Array.isArray(seen) ? seen : {};
  } catch {
    return {};
  }
};

// Sent on the window when what was seen changes, for the counts of unseen imports to follow.
export const AI_IMPORT_SEEN_EVENT = 'bulk-import:ai-import-seen';

// Keeps the statuses shown now, for the imports still in the list.
export const markRunsSeen = (statuses: TSeenStatuses) => {
  const kept = new Set(readAiRunIds());
  const before = JSON.stringify(readSeenStatuses());
  const seen = JSON.stringify(
    Object.fromEntries(
      Object.entries({ ...readSeenStatuses(), ...statuses }).filter(([runId, status]) => kept.has(runId) && status !== 'running')
    )
  );
  if (seen === before) return;
  try {
    localStorage.setItem(LOCAL_STORE_ITEMS.AI_IMPORT_SEEN, seen);
  } catch {}
  window.dispatchEvent(new Event(AI_IMPORT_SEEN_EVENT));
};

export const isRunSeen = (runId: string, status: string | undefined, seen: TSeenStatuses = readSeenStatuses()) =>
  !!status && status !== 'running' && seen[runId] === status;

// The query parameter of the bulk import page that opens an import on arriving (from the dashboard).
export const OPEN_RUN_PARAM = 'openRun';

// Where the file of an import is kept in IndexedDB, so its review can still save the source file
// after a reload.
export const savedAiFileId = (runId: string) => `${SAVED_AI_FILE_ID}:${runId}`;

// The browser's sha256 of a file as chosen (before the xlsx is normalized for the upload), to tell the
// same file again; null where the browser cannot hash it.
export const fileSha256 = async (file: Blob): Promise<string | null> => {
  try {
    const subtle = globalThis.crypto?.subtle;
    if (!subtle || typeof file.arrayBuffer !== 'function') return null;
    const digest = await subtle.digest('SHA-256', await file.arrayBuffer());
    return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
};
